import { findParentNodeClosestToPos } from '@tiptap/core'
import { isChangeOrigin } from '@tiptap/extension-collaboration'
import { Node as ProseMirrorNode, ResolvedPos, Schema } from '@tiptap/pm/model'
import { EditorState, Plugin, PluginKey, Transaction } from '@tiptap/pm/state'
import { LIST_TYPE_ORDERED } from '../../../../../../backend/src/tiptap/editor/extensions/marks/suggestion'
import { MINUTE_NODE_INTERNAL_TYPE, MINUTE_TREE_INTERNAL_TYPE } from '../../../../../../backend/src/tiptap/editor/extensions/shared/minute-internal-types'
import { isAuthoritativeContent } from './authoritative-content'
import { buildEmptyListItem, isListNode } from './list-track-changes'
import { TRACK_CHANGES_SKIP_META, trackChangesPluginKey } from './track-changes-plugin'

/**
 * Keeps the minutes tree — the numbered list the agenda items live in — intact.
 *
 * Everything downstream of the editor finds the agenda items by walking into a single `MinuteTree` node: a
 * targeted regeneration splices into it, the exports read structure out of it, and citations hang off the items
 * inside it. So the document is held to two rules. There is exactly one tree, restored empty if an edit deletes
 * it outright. And the tree stays a numbered list, which is what makes its direct children — the top level of
 * the agenda — numbered rather than bulleted.
 *
 * Neither rule is imposed on a document that has no tree to begin with: a call whose minutes have not been
 * generated is shown its outline instead, and that is not an agenda.
 */

export type MinuteTreePlacement = {
  node: ProseMirrorNode
  from: number
  to: number
}

type MinuteTreePluginState = {
  trees: MinuteTreePlacement[]
}

const minuteTreePluginKey = new PluginKey<MinuteTreePluginState>('minuteTree')

export function isMinuteTree(node: ProseMirrorNode): boolean {
  return node.attrs['internalType'] === MINUTE_TREE_INTERNAL_TYPE
}

function isNumberedList(node: ProseMirrorNode): boolean {
  return node.type.name === LIST_TYPE_ORDERED
}

/**
 * Every minutes tree in the document, with the range it occupies. A well-formed document holds one; the rest of
 * this module is what keeps it that way, so the plural is the point.
 *
 * A found tree is not descended into. It holds nearly the whole document, and a tree nested inside another one
 * is not what the invariant is about — a second tree beside the first is.
 */
export function findMinuteTrees(doc: ProseMirrorNode): MinuteTreePlacement[] {
  const trees: MinuteTreePlacement[] = []
  doc.descendants((node, position) => {
    if (!isMinuteTree(node)) {
      return true
    }
    trees.push({ node, from: position, to: position + node.nodeSize })
    return false
  })
  return trees
}

/**
 * Whether a transaction removes a tree's content rather than merely unwrapping it.
 *
 * Losing the tree by deleting the agenda is allowed — an empty one is put back in its place. Losing it while its
 * items stay behind is not: that is a list being lifted apart, and it would strand the agenda as loose text.
 */
function isTreeDeleted(transaction: Transaction, tree: MinuteTreePlacement): boolean {
  return transaction.mapping.map(tree.from, 1) >= transaction.mapping.map(tree.to, -1)
}

/**
 * Whether the document a transaction produces still holds the agenda the way the editor found it: no second
 * tree, none of them turned into a bullet list, and none of them unwrapped.
 */
function keepsMinuteTreeIntact(transaction: Transaction, trees: MinuteTreePlacement[]): boolean {
  const remaining = findMinuteTrees(transaction.doc)

  return (
    remaining.length <= trees.length &&
    remaining.every(({ node }) => isNumberedList(node)) &&
    (remaining.length === trees.length || trees.every((tree) => isTreeDeleted(transaction, tree)))
  )
}

/**
 * Whether a transaction is this user's own edit, rather than another client's replayed here by the sync layer
 * or the server's own copy of the document handed back by a reset.
 */
function isLocalDocumentChange(transaction: Transaction): boolean {
  return transaction.docChanged && !isChangeOrigin(transaction) && !isAuthoritativeContent(transaction)
}

function buildEmptyMinuteTree(schema: Schema): ProseMirrorNode {
  return schema.nodes[LIST_TYPE_ORDERED].create(
    { internalType: MINUTE_TREE_INTERNAL_TYPE },
    buildEmptyListItem(schema, { internalType: MINUTE_NODE_INTERNAL_TYPE })
  )
}

/**
 * Puts an empty agenda back at the end of the document. The restored tree is scaffolding rather than an edit
 * anybody made, so track changes is told to leave it alone.
 */
function restoreEmptyMinuteTree(state: EditorState): Transaction {
  const transaction = state.tr.insert(state.doc.content.size, buildEmptyMinuteTree(state.schema))
  return transaction.setMeta(trackChangesPluginKey, TRACK_CHANGES_SKIP_META)
}

function isInsideMinuteTreeTopLevel($position: ResolvedPos): boolean {
  const list = findParentNodeClosestToPos($position, isListNode) ?? null
  return list !== null && isMinuteTree(list.node)
}

/**
 * Whether the list a bullet/number toggle would rewrite is the minutes tree itself, which is the case exactly
 * when the selection sits in a top-level agenda item rather than in one of its subitems.
 */
export function isMinuteTreeListTypeLocked(state: EditorState): boolean {
  const { $from, $to } = state.selection
  return isInsideMinuteTreeTopLevel($from) || isInsideMinuteTreeTopLevel($to)
}

export function createMinuteTreePlugin(): Plugin<MinuteTreePluginState> {
  return new Plugin<MinuteTreePluginState>({
    key: minuteTreePluginKey,

    state: {
      init(_, state) {
        return { trees: findMinuteTrees(state.doc) }
      },
      apply(transaction, pluginState, _oldState, newState) {
        return transaction.docChanged ? { trees: findMinuteTrees(newState.doc) } : pluginState
      },
    },

    // A remote transaction carries an edit another client already made, so rejecting it here would only push the
    // two documents out of sync. Every client runs this guard on its own edits instead.
    filterTransaction(transaction, state) {
      if (!isLocalDocumentChange(transaction)) {
        return true
      }
      const trees = minuteTreePluginKey.getState(state)?.trees ?? []
      return trees.length === 0 || keepsMinuteTreeIntact(transaction, trees)
    },

    // Likewise the restore: whoever deleted the agenda puts it back, and the rest of the clients receive that.
    appendTransaction(transactions, oldState, newState) {
      const trees = minuteTreePluginKey.getState(oldState)?.trees ?? []
      if (trees.length === 0 || !transactions.some(isLocalDocumentChange)) {
        return null
      }
      // The plugin state has already been recomputed against `newState`, so there is nothing to walk again here.
      return minuteTreePluginKey.getState(newState)?.trees.length === 0 ? restoreEmptyMinuteTree(newState) : null
    },
  })
}
